// IndexedDB-opslag van activiteiten. DEZELFDE database als de vorige (vanilla) versie:
// `GameOnActivitiesDB` v1, store `activities` (keyPath 'id', autoIncrement), zodat bestaande
// gebruikers hun lijst en scores behouden. Deze laag doet alleen I/O; opschonen gebeurt in de state-laag.
import type { Activity } from '../domain/types';
import { IDB_NAME, IDB_STORE, IDB_VERSION } from './keys';

let dbPromise: Promise<IDBDatabase> | null = null;

/** Eén gedeelde verbinding; bij versionchange/close/fout wordt bij de volgende call opnieuw geopend. */
export function openActivityDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const p = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is niet beschikbaar in deze browser'));
      return;
    }
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        if (dbPromise === p) dbPromise = null;
      };
      db.onclose = () => {
        if (dbPromise === p) dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error('IndexedDB openen mislukt'));
    request.onblocked = () => console.warn('IndexedDB openen geblokkeerd door een andere tab');
  });
  dbPromise = p;
  p.catch(() => {
    if (dbPromise === p) dbPromise = null;
  });
  return p;
}

/** Sluit de gedeelde verbinding (tests, of vóór verwijderen van de database). */
export async function closeActivityDB(): Promise<void> {
  const p = dbPromise;
  dbPromise = null;
  if (!p) return;
  try {
    (await p).close();
  } catch {
    // al dicht of nooit geopend
  }
}

/** Alle ruwe records (nog niet opgeschoond; oudere records kunnen afwijkende velden hebben). */
export async function loadAllActivities(): Promise<Activity[]> {
  const db = await openActivityDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).getAll();
    req.onsuccess = () => resolve((req.result as Activity[]) ?? []);
    req.onerror = () => reject(req.error ?? new Error('Lezen mislukt'));
  });
}

/**
 * Vervangt de volledige lijst in één transactie (clear + put). Faalt één record, dan wordt
 * alles teruggedraaid en blijft de oude lijst staan. Resolvet pas op `complete`.
 */
export async function saveAllActivities(list: Activity[]): Promise<void> {
  const db = await openActivityDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('Transactie afgebroken'));
    try {
      store.clear();
      for (const act of list) store.put(act);
    } catch (e) {
      try {
        tx.abort();
      } catch {
        // transactie was al afgebroken
      }
      reject(e);
    }
  });
}

/** Verhoogt de score met 1; resolvet (true = gevonden) pas als de transactie is weggeschreven. */
export async function incrementActivityScore(id: number): Promise<boolean> {
  if (!Number.isSafeInteger(id) || id <= 0) return false;
  const db = await openActivityDB();
  return new Promise<boolean>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    let updated = false;
    const req = store.get(id);
    req.onsuccess = () => {
      const act = req.result as Activity | undefined;
      if (act) {
        act.score = (Number(act.score) || 0) + 1;
        store.put(act);
        updated = true;
      }
    };
    tx.oncomplete = () => resolve(updated);
    tx.onabort = () => reject(tx.error ?? new Error('Transactie afgebroken'));
  });
}

export function isQuotaError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { name?: string; message?: string };
  return err.name === 'QuotaExceededError' || /quota/i.test(String(err.message ?? ''));
}
